'use client';

import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import type { PaginationState } from '@hs/ui';
import {
  Avatar,
  Button,
  cn,
  Input,
  ListBox,
  Pagination,
  Select,
  Separator,
  TextField,
  toast,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import {
  IconChevronLeft,
  IconDownload as Download,
  IconRefresh as RefreshCw,
} from '@tabler/icons-react';
import { formatDateTime } from '@hs/shared/utils/formatTime';
import { apiClient } from '@hs/shared/utils/api-client';

const ORG_ALL_ID = '__all__';
const PRIZE_ALL_ID = '__all__';
const DEFAULT_PAGE_SIZE = 20;

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

type WinnerRowInviter = {
  id?: string | null;
  username?: string;
  nickname?: string | null;
  avatarUrl?: string | null;
};
type WinnerRowPeople = {
  id?: string | null;
  fullName?: string;
  jobNumber?: string | null;
  avatarUrl?: string | null;
};
type WinnerRowOrg = { id?: string | null; shortName?: string };
type WinnerRowPrize = { id?: string | null; name?: string };

type WinnerRow = {
  id: string;
  activityId?: string | null;
  userId?: string | null;
  inviterId?: string | null;
  orgId?: string | null;
  name?: string;
  gender?: string | null;
  mobile?: string;
  status?: string;
  isWinner?: boolean;
  prizeId?: string | null;
  wonAt?: Date | string | null;
  registeredAt?: Date | string | null;
  createdAt?: Date | string | null;
  inviter?: WinnerRowInviter | null;
  people?: WinnerRowPeople | null;
  org?: WinnerRowOrg | null;
  prize?: WinnerRowPrize | null;
  [key: string]: unknown;
};

function genderSuffix(gender: string | null | undefined): string {
  if (gender === 'MALE') return '先生';
  if (gender === 'FEMALE') return '女士';
  return '';
}

const CN_MOBILE_REGEX = /^1[3-9]\d{9}$/;

function isValidCnMobile(value: string): boolean {
  return CN_MOBILE_REGEX.test(value.trim());
}

/**
 * 活动中奖名单页
 * 路由：/activities/:id/winners
 */
export default function ActivityWinnersPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [unitValue, setUnitValue] = React.useState<string | null>(null);
  const [prizeValue, setPrizeValue] = React.useState<string | null>(null);
  const [keyword, setKeyword] = React.useState('');
  const [keywordInput, setKeywordInput] = React.useState('');
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [exportSignOffLoading, setExportSignOffLoading] = React.useState(false);

  const { data: activity, isLoading: activityLoading } = trpc.activities.get.useQuery(
    { id: activityId! },
    { enabled: !!activityId }
  );

  const { data: orgs = [] } = trpc.orgs.findAllRoots.useQuery(undefined, {
    enabled: !!activityId,
  });

  const { data: prizes = [] } = trpc.activityPrizes.listAll.useQuery(
    { activityId: activityId! },
    { enabled: !!activityId }
  );

  const page = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const queryInput = React.useMemo(
    () => ({
      activityId: activityId!,
      isWinner: true as const,
      orgId: unitValue ?? undefined,
      prizeId: prizeValue ?? undefined,
      keyword: keyword.trim() || undefined,
      where: undefined as Record<string, unknown> | undefined,
      page,
      pageSize,
      sortBy: 'createdAt' as const,
      sortOrder: 'desc' as const,
    }),
    [activityId, unitValue, prizeValue, keyword, page, pageSize]
  );

  const {
    data,
    isLoading: listLoading,
    refetch,
    isFetching,
  } = trpc.activityRegistrations.list.useQuery(queryInput, {
    enabled: !!activityId,
  });

  const items: WinnerRow[] = React.useMemo(
    () => (data?.items ?? []).map((r) => ({ ...r, id: String(r.id ?? '') })),
    [data?.items]
  );
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (pageSize || DEFAULT_PAGE_SIZE)));
  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = React.useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );
  const pageSizes = [10, 20, 50, 100];

  const handleSearch = React.useCallback(() => {
    const trimmed = keywordInput.trim();
    if (trimmed === '') {
      setKeyword('');
      setPagination((p) => ({ ...p, pageIndex: 0 }));
      return;
    }
    if (!isValidCnMobile(trimmed)) {
      toast.danger('请输入正确的 11 位中国手机号');
      return;
    }
    setKeyword(trimmed);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [keywordInput]);

  const handleExportSignOff = React.useCallback(async () => {
    if (!activityId) {
      toast.danger('无法导出：缺少活动 ID');
      return;
    }
    setExportSignOffLoading(true);
    try {
      const params: Record<string, string> = {
        activityId,
        isWinner: 'true',
      };
      if (unitValue) params.orgId = unitValue;
      if (prizeValue) params.prizeId = prizeValue;
      if (keyword.trim()) params.keyword = keyword.trim();
      await apiClient.downloadFile('activity_registrations/export/sign-off', params, '签收表.xlsx');
      toast.success('签收表已开始下载');
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : '导出签收表失败');
    } finally {
      setExportSignOffLoading(false);
    }
  }, [activityId, unitValue, prizeValue, keyword]);

  const orgSelectValue = unitValue ?? ORG_ALL_ID;
  const prizeSelectValue = prizeValue ?? PRIZE_ALL_ID;

  const columns = React.useMemo<DataGridColumn<WinnerRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: '中奖人姓名',
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const name = row.name ?? '';
          const suffix = genderSuffix(row.gender);
          const display = name ? `${name.slice(0, 1)}${suffix}` : '—';
          return <span className="text-sm font-medium">{display}</span>;
        },
      },
      {
        id: 'mobile',
        accessorKey: 'mobile',
        header: '中奖人手机号',
        minWidth: 130,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-sm tabular-nums">{row.mobile ?? '—'}</span>,
      },
      {
        id: 'prize',
        accessorKey: 'prize',
        header: '奖品名称',
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-sm">{row.prize?.name ?? '—'}</span>,
      },
      {
        id: 'wonAt',
        accessorKey: 'wonAt',
        header: '中奖时间',
        minWidth: 160,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.wonAt ? formatDateTime(row.wonAt as string | Date) : '—'}
          </span>
        ),
      },
      {
        id: 'inviter',
        accessorKey: 'inviter',
        header: '邀请人',
        minWidth: 160,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const inviter = row.inviter;
          const people = row.people;
          const fullName = people?.fullName ?? '—';
          const avatarUrl = people?.avatarUrl ?? inviter?.avatarUrl;
          if (!inviter && !people) return <span className="text-muted-foreground text-sm">—</span>;
          return (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 shrink-0" variant="soft">
                {avatarUrl ? <Avatar.Image src={avatarUrl} alt="" /> : null}
                <Avatar.Fallback className="text-xs">
                  {fullName !== '—' ? fullName.slice(0, 1) : '?'}
                </Avatar.Fallback>
              </Avatar>
              <span className="text-sm">{fullName}</span>
            </div>
          );
        },
      },
      {
        id: 'jobNumber',
        accessorKey: 'people',
        header: '邀请人工号',
        minWidth: 100,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-sm">{row.people?.jobNumber ?? '—'}</span>,
      },
      {
        id: 'org',
        accessorKey: 'org',
        header: '所在组织',
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="text-sm">{row.org?.shortName ?? '—'}</span>,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: '状态',
        minWidth: 96,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const s = row.status;
          const label =
            s === 'WON'
              ? '已中奖'
              : s === 'CONFIRMED'
                ? '已确认'
                : s === 'PENDING'
                  ? '待确认'
                  : (s ?? '—');
          return <span className="text-sm">{label}</span>;
        },
      },
    ],
    []
  );

  const columnsWithRowHeader = React.useMemo(() => {
    const first = columns[0];
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
    }));
  }, [columns]);

  if (!activityId) {
    return (
      <div className="text-muted-foreground p-4 text-sm">缺少活动 ID，请从活动列表进入中奖名单。</div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-4xl">
        <header className="flex shrink-0 flex-wrap items-center gap-3 px-4 py-3">
          <Button isIconOnly variant="tertiary" className="mr-4" onPress={() => navigate("/activities")}>
            <IconChevronLeft className="size-6" stroke={1.5} />
          </Button>
          <span className="text-foreground min-w-0 flex-1 truncate text-2xl font-semibold tracking-tight">
            {activityLoading ? '加载中…' : `${activity?.title ?? '活动'} · 中奖名单`}
          </span>
        </header>

        <div className={cn('flex min-h-0 w-full flex-1 flex-col p-4')}>
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              className={cn(
                'grid min-h-0 min-w-0 w-full flex-1 gap-y-3',
                'grid-rows-[auto_minmax(0,1fr)_auto]'
              )}
              data-slot="winners-grid-main"
            >
              <div className="relative flex min-w-0 shrink-0 flex-col gap-3">
                <div
                  className="flex w-full min-w-0 shrink-0 flex-nowrap items-center justify-between gap-x-4 py-1"
                  data-slot="winners-toolbar"
                >
                  <div
                    className="flex min-h-10 min-w-0 flex-1 flex-nowrap items-center justify-start gap-2 overflow-x-auto overscroll-x-contain px-2 py-1.5 [scrollbar-width:thin]"
                    data-slot="winners-action-toolbar"
                  >
                    <TextField className="w-[min(100%,220px)] min-w-[160px] shrink-0">
                      <Input
                        placeholder="搜索手机号（11 位）"
                        value={keywordInput}
                        maxLength={11}
                        inputMode="numeric"
                        autoComplete="off"
                        onChange={(e) => setKeywordInput(e.target.value)}
                      />
                    </TextField>
                    <Button variant="secondary" size="sm" className="shrink-0" onPress={handleSearch}>
                      搜索
                    </Button>

                    <Select
                      className="w-40"
                      value={orgSelectValue}
                      onChange={(key) => {
                        const k = key != null ? String(key) : ORG_ALL_ID;
                        setUnitValue(k === ORG_ALL_ID ? null : k);
                        setPagination((p) => ({ ...p, pageIndex: 0 }));
                      }}
                    >
                      <Select.Trigger className="h-9 min-h-9 text-sm">
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          <ListBox.Item id={ORG_ALL_ID} textValue="全部组织">
                            全部组织
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                          {orgs.map((org) => {
                            const oid = org.id ?? '';
                            return (
                              <ListBox.Item key={oid} id={oid} textValue={org.shortName ?? org.fullName ?? '未命名'}>
                                {org.shortName ?? org.fullName ?? '未命名'}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            );
                          })}
                        </ListBox>
                      </Select.Popover>
                    </Select>

                    <Select
                      className="w-50"
                      value={prizeSelectValue}
                      onChange={(key) => {
                        const k = key != null ? String(key) : PRIZE_ALL_ID;
                        setPrizeValue(k === PRIZE_ALL_ID ? null : k);
                        setPagination((p) => ({ ...p, pageIndex: 0 }));
                      }}
                    >
                      <Select.Trigger className="h-9 min-h-9 text-sm">
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          <ListBox.Item id={PRIZE_ALL_ID} textValue="全部奖品">
                            全部奖品
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                          {prizes.map((prize) => {
                            const pid = String(prize.id ?? '');
                            return (
                              <ListBox.Item key={pid} id={pid} textValue={prize.name ?? '未命名'}>
                                {prize.name ?? '未命名'}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            );
                          })}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  </div>

                  <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2">
                    <Button variant="secondary" size="sm" isDisabled={isFetching} onPress={() => void refetch()}>
                      <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} stroke={1.5} />
                      刷新
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      isDisabled={exportSignOffLoading || total === 0}
                      onPress={() => void handleExportSignOff()}
                    >
                      <Download
                        className={cn('size-4', exportSignOffLoading && 'animate-pulse')}
                        stroke={1.5}
                      />
                      {exportSignOffLoading ? '导出中…' : '导出签收表'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                  {listLoading ? (
                    <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                      加载中…
                    </div>
                  ) : null}
                  <DataGrid<WinnerRow>
                    allowsColumnResize
                    aria-label="中奖名单"
                    className="flex h-full min-h-0 min-w-0 flex-col !overflow-visible"
                    columns={columnsWithRowHeader}
                    contentClassName="min-w-0 !overflow-visible"
                    data={items}
                    getRowId={(item) => item.id}
                    renderEmptyState={() => (
                      <div className="text-muted-foreground py-8 text-center text-sm">暂无中奖记录</div>
                    )}
                    scrollContainerClassName={cn(
                      'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                      '[&_[data-slot=table-resizable-container]]:overflow-visible',
                      '[&_[data-slot=table-content]]:!overflow-visible',
                      '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                      '[&_th]:min-w-0 [&_td]:min-w-0',
                      '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
                    )}
                    selectionMode="none"
                    showSelectionCheckboxes={false}
                    variant="primary"
                  />
                </div>
              </div>

              <div
                className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm"
                data-slot="winners-pagination"
              >
                <div className="flex w-fit shrink-0 items-center">
                  <Pagination className="w-fit max-w-none" size="sm">
                    <Pagination.Content className="flex w-fit min-w-0">
                      <Pagination.Item>
                        <Pagination.Previous
                          isDisabled={safePage <= 1}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.max(0, p.pageIndex - 1),
                            }))
                          }
                        >
                          <Pagination.PreviousIcon />
                        </Pagination.Previous>
                      </Pagination.Item>
                      {paginationPages.map((p, i) =>
                        p === 'ellipsis' ? (
                          <Pagination.Item key={`e-${i}`}>
                            <Pagination.Ellipsis />
                          </Pagination.Item>
                        ) : (
                          <Pagination.Item key={p}>
                            <Pagination.Link
                              isActive={p === safePage}
                              onPress={() =>
                                setPagination((prev) => ({
                                  ...prev,
                                  pageIndex: (p as number) - 1,
                                }))
                              }
                            >
                              {p}
                            </Pagination.Link>
                          </Pagination.Item>
                        )
                      )}
                      <Pagination.Item>
                        <Pagination.Next
                          isDisabled={safePage >= pageCount}
                          onPress={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                            }))
                          }
                        >
                          <Pagination.NextIcon />
                        </Pagination.Next>
                      </Pagination.Item>
                    </Pagination.Content>
                  </Pagination>
                </div>

                <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                  <InlineSelect
                    aria-label="每页条数"
                    value={String(pagination.pageSize)}
                    onChange={(v) => {
                      if (v) {
                        setPagination((p) => ({
                          ...p,
                          pageSize: Number(v),
                          pageIndex: 0,
                        }));
                      }
                    }}
                  >
                    <InlineSelect.Trigger>
                      <span className="text-muted">每页</span>
                      <InlineSelect.Value />
                      <InlineSelect.Indicator />
                    </InlineSelect.Trigger>
                    <InlineSelect.Popover className="w-[88px]">
                      <ListBox>
                        {pageSizes.map((n) => (
                          <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                            {n}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </InlineSelect.Popover>
                  </InlineSelect>
                  <Separator className="!h-4 shrink-0" orientation="vertical" />
                  <span className="text-muted shrink-0">
                    第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                    {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}
